<template>
  <Card class="tw-mt-4">
    <CardHeader class="tw-pb-3">
      <CardTitle class="tw-text-base tw-font-medium tw-text-neutral-700 dark:tw-text-neutral-300">
        {{ t('orders.titles.related_orders') }}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <!-- Loading State -->
      <div v-if="isLoading" class="tw-space-y-3">
        <Skeleton class="tw-h-10 tw-w-full" />
        <Skeleton class="tw-h-10 tw-w-full" />
        <Skeleton class="tw-h-10 tw-w-full" />
      </div>

      <!-- Content -->
      <div v-else>
        <Accordion type="multiple" :default-value="['upcoming', 'past']" class="tw-w-full">
          <!-- Upcoming Orders -->
          <AccordionItem v-if="upcomingOrders.length" value="upcoming" class="tw-border-b-0">
            <AccordionTrigger class="tw-py-3 hover:tw-no-underline">
              <div class="tw-flex tw-items-center tw-gap-2">
                <span class="tw-font-medium">{{ t('orders.titles.upcoming_orders') }}</span>
                <Badge variant="secondary" class="tw-ml-1">
                  {{ upcomingOrders.length }}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div class="tw-space-y-2 tw-pt-1">
                <router-link
                  v-for="relatedOrder in upcomingOrders"
                  :key="relatedOrder?.id"
                  :to="{ name: 'order', params: { id: relatedOrder.id } }"
                  class="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3 tw-transition-colors hover:tw-bg-neutral-100 dark:tw-border-neutral-800 dark:tw-bg-neutral-900 dark:hover:tw-bg-neutral-800"
                >
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <Badge :variant="getStatusVariant(relatedOrder?.status_display)">
                      {{ getStatusLabel(relatedOrder?.status_display) }}
                    </Badge>
                    <span
                      class="tw-text-sm tw-font-medium tw-text-neutral-700 dark:tw-text-neutral-300"
                    >
                      {{ relatedOrder?.order_number }}
                    </span>
                  </div>
                  <span class="tw-text-sm tw-text-neutral-500 dark:tw-text-neutral-400">
                    {{ relatedOrder?.service_date }}
                  </span>
                </router-link>
              </div>
            </AccordionContent>
          </AccordionItem>

          <!-- Past Orders -->
          <AccordionItem v-if="pastOrders.length" value="past" class="tw-border-b-0">
            <AccordionTrigger class="tw-py-3 hover:tw-no-underline">
              <div class="tw-flex tw-items-center tw-gap-2">
                <span class="tw-font-medium">{{ t('orders.titles.past_orders') }}</span>
                <Badge variant="secondary" class="tw-ml-1">
                  {{ pastOrders.length }}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div class="tw-space-y-2 tw-pt-1">
                <router-link
                  v-for="relatedOrder in pastOrders"
                  :key="relatedOrder.id"
                  :to="{ name: 'order', params: { id: relatedOrder.id } }"
                  class="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-3 tw-transition-colors hover:tw-bg-neutral-100 dark:tw-border-neutral-800 dark:tw-bg-neutral-900 dark:hover:tw-bg-neutral-800"
                >
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <Badge :variant="getStatusVariant(relatedOrder?.status_display)">
                      {{ getStatusLabel(relatedOrder?.status_display) }}
                    </Badge>
                    <span
                      class="tw-text-sm tw-font-medium tw-text-neutral-700 dark:tw-text-neutral-300"
                    >
                      {{ relatedOrder?.order_number }}
                    </span>
                  </div>
                  <span class="tw-text-sm tw-text-neutral-500 dark:tw-text-neutral-400">
                    {{ relatedOrder?.service_date }}
                  </span>
                </router-link>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <!-- Empty State -->
        <div
          v-if="!upcomingOrders.length && !pastOrders.length"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center"
        >
          <div class="tw-text-neutral-400 dark:tw-text-neutral-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="tw-mx-auto tw-h-12 tw-w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p class="tw-mt-2 tw-text-sm tw-text-neutral-500 dark:tw-text-neutral-400">
            {{ t('orders.titles.no_related_orders') }}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useI18n } from 'vue-i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const { t } = useI18n();

const { getRecurringInstructionChildren } = useRecurringInstructionStore();

const props = defineProps({
  order: {
    type: Object,
    default: () => ({}),
  },
  recurringInstructionId: {
    type: Number,
    default: null,
  },
});

const upcomingOrders = ref<any[]>([]);
const pastOrders = ref<any[]>([]);
const isLoading = ref(true);

const loadOrders = async (parentId: number) => {
  isLoading.value = true;
  try {
    const response = await getRecurringInstructionChildren(parentId);
    if (props.recurringInstructionId) {
      upcomingOrders.value = response;
    } else {
      pastOrders.value = response.filter(
        (order: any) => order.order_number_without_prefix < props.order.order_number_without_prefix
      );
      upcomingOrders.value = response.filter(
        (order: any) => order.order_number_without_prefix > props.order.order_number_without_prefix
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const getStatusVariant = (status: string): BadgeVariant => {
  const statusMap: Record<string, BadgeVariant> = {
    APPROVED: 'approved',
    CLEARING: 'clearing',
    REJECTED: 'rejected',
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending',
  };
  return statusMap[status] || 'secondary';
};

const getStatusLabel = (status: string) => {
  const labelMap: Record<string, string> = {
    APPROVED: t('orders.titles.approved'),
    CLEARING: t('orders.titles.clearing'),
    REJECTED: t('orders.titles.rejected'),
    DRAFT: t('orders.titles.draft'),
    PENDING_APPROVAL: t('requests.titles.pending'),
  };
  return labelMap[status] || status;
};

onMounted(() => {
  const parentId = props.recurringInstructionId
    ? props.recurringInstructionId
    : props.order.recurring_instruction;
  loadOrders(parentId);
});

watch(
  () => props.order,
  () => {
    const parentId = props.recurringInstructionId
      ? props.recurringInstructionId
      : props.order.recurring_instruction;
    loadOrders(parentId);
  }
);
</script>
